// Kundenliste (/rechnung/kunden): Antworten des Servers (server/src/modules/rechnungstool/kunden.ts) und kleine Helfer
import type { FirmaSchlagwort, KontaktDatei, Person } from './kontakte/KontaktDaten';
import { landName as landNameWelt } from './laender-welt';
import type { EFormat, KundenArt, RtArt, RtKind } from './types';
import type { Waehrung } from './waehrung';

// Kunde mit Zahlen (Beträge in Euro, Umsatz netto; Belege in anderer Währung zum Kurs des Belegs umgerechnet)
export interface KundeVoll {
  id: string;
  nummer: number;
  name: string;
  extra: string | null;
  street: string;
  zip: string;
  city: string;
  country: string;
  art: KundenArt;
  vatId: string | null;
  email: string | null;
  emailCc: string | null;
  phone: string | null;
  buyerRef: string | null;
  xrechnung: boolean;
  // leer: Zahlungsziel aus „Meine Angaben“
  paymentDays: number | null;
  notiz: string | null;
  // nicht automatisch mahnen (Mahnlauf)
  mahnstopp: boolean;
  // Währung für neue Belege; fremd: Rechnungen in anderer Währung (in den Zahlen umgerechnet)
  waehrung: Waehrung;
  fremd: boolean;
  umsatzJahr: number;
  umsatzVorjahr: number;
  umsatzGesamt: number;
  offen: number;
  offenAnzahl: number;
  ueberfaellig: number;
  ueberfaelligAnzahl: number;
  rechnungen: number;
  letzteRechnung: { id: string; number: string; invoiceDate: string } | null;
  // Ø Tage vom Rechnungsdatum bis zur Zahlung (bezahlte Rechnungen der letzten 12 Monate)
  zahlungsdauer: number | null;
  warnung: 'ueberfaellig' | 'langsam' | null;
  // zum Ordnen und Filtern (kontakte/Schlagwoerter.tsx)
  schlagwoerter: string[];
}

// in der Liste: Namen und E-Mails der Ansprechpartner (klein geschrieben) für die Suche
export interface KundeZeile extends KundeVoll {
  suchtext: string;
}

export interface KundenAntwort {
  jahr: number;
  heute: string;
  naechsteNummer: number | null;
  // Schlagwörter der Firma (Kunden und Lieferanten) für Filter und Vorschläge
  schlagwoerter: FirmaSchlagwort[];
  kunden: KundeZeile[];
}

export interface KundeRechnung {
  id: string;
  kind: RtKind;
  art: RtArt;
  number: string;
  invoiceDate: string;
  dueDate: string;
  net: number;
  amount: number;
  status: 'offen' | 'teilweise' | 'bezahlt' | 'storniert';
  offen: number;
  erstattung: number;
  // ganz ausgebucht, ohne Geld
  ausgebucht: boolean;
  paidOn: string | null;
  stornoOfNumber: string | null;
  mahnstufe: number;
  eFormat: EFormat | null;
  // Beträge der Zeile in dieser Währung
  waehrung: Waehrung;
}

export interface KundeAntwort {
  jahr: number;
  heute: string;
  kunde: KundeVoll;
  rechnungen: KundeRechnung[];
  // Billtoro: Zeiten zu diesem Kunden in der Sicht des Logins (noch nicht berechnet: Minuten und Einträge); null:
  // keine oder Kanzlei
  zeiten?: { offenMinuten: number; eintraege: number } | null;
  personen: Person[];
  // Dateien am Kunden (Billtoro; Kanzlei-App: null), höchstens dateienMb je Datei
  dateien: KontaktDatei[] | null;
  dateienMb?: number;
}

// Vorschau des Imports; lieferant: nur Billtoro (Lieferanten kommen in die Lieferantenliste), uebersprungen: nur die
// Kanzlei-App (Lieferanten bleiben draußen)
export type ImportStatus = 'neu' | 'doppelt' | 'fehler' | 'uebersprungen' | 'lieferant';
export interface ImportVorschau {
  format: string;
  spalten: { feld: string; spalte: string }[];
  anzahl: Partial<Record<ImportStatus, number>>;
  zeilen: {
    zeile: number;
    status: ImportStatus;
    name: string;
    ort: string;
    email: string | null;
    nummer: number | null;
    hinweise: string[];
    fehler: string | null;
    kunde: Record<string, unknown> | null;
    // nur Billtoro
    art?: 'kunde' | 'lieferant';
    lieferant?: Record<string, unknown> | null;
  }[];
}

export const ART_NAME: Record<KundenArt, string> = { firma: 'Firma', privat: 'Privatperson', behoerde: 'Behörde' };

export const landName = (code: string) => landNameWelt(code);

// Ort für die Karte: „20095 Hamburg“, im Ausland mit Land (außerhalb der EU auch ohne PLZ)
export const ortOf = (k: { zip: string; city: string; country: string }) =>
  `${`${k.zip} ${k.city}`.trim()}${k.country !== 'DE' ? `, ${landName(k.country)}` : ''}`;

export const kundenUrl = (firmId: string) => `/api/rechnungstool/${firmId}/kunden`;
export const kundeUrl = (id: string) => `/api/rechnungstool/kunde/${id}`;

// „1 Rechnung“, „3 Rechnungen“
export const rechnungen = (n: number) => `${n} ${n === 1 ? 'Rechnung' : 'Rechnungen'}`;
export const tage = (n: number) => `${n} ${n === 1 ? 'Tag' : 'Tage'}`;
